import get from "lodash/get";
import ApiClient from "lib/ApiClient";
import flattenContnetfulMedia from "utils/flattenContentfulMedia";
import stringToScreamingSnakeCase from "utils/stringToScreamingSnakeCase";

import {
  GlobalSettings,
  ContentfulProducts,
  AssociatedVariantValues
} from "types";

export default {
  fetchGlobalSettings: (): Promise<GlobalSettings> => {
    return ApiClient.getEntries({
      content_type: "global",
      include: 4
    }).then(data => {
      const fields = get(data, "items[0].fields", {});
      const instagramLink = get(fields, "instagramLink", "");
      const termsConditions = get(fields, "shopTermsConditions", "");
      const faq = get(fields, "shopFaq", []).map((content: unknown) => {
        const fields = get(content, "fields", {});
        return {
          title: get(fields, "title", ""),
          content: get(fields, "content", "")
        };
      });
      const additionalReadings = get(fields, "shopAdditionalReadings", []).map(
        (article: unknown, i: number) => {
          const id = get(article, "sys.id", i);
          const fields = get(article, "fields", {});
          const articleType = stringToScreamingSnakeCase(
            get(fields, "articleType", "")
          );
          const thumbnail = flattenContnetfulMedia(
            get(fields, "thumbnail", {})
          );
          const title = get(fields, "title", "");
          const slug = get(fields, "slug", "");

          return {
            id,
            articleType,
            thumbnail,
            title,
            slug
          };
        }
      );

      const shopProducts = get(fields, "shopProducts", []).map(
        (product: unknown) => get(product, "fields.productHandle", "")
      );

      return {
        instagramLink,
        additionalReadings,
        termsConditions,
        faq,
        shopProducts
      };
    });
  },
  fetchProducts: (): Promise<ContentfulProducts> => {
    return ApiClient.getEntries({
      content_type: "product",
      include: 4
    }).then(data => {
      return get(data, "items", []).map((item: unknown) => {
        const fields = get(item, "fields", {});
        const title = get(fields, "title", "");
        const productHandle = get(fields, "productHandle", "");
        const care = get(fields, "care", "");
        const fit = get(fields, "fit", "");
        const details = get(fields, "details", "");
        const productStickerLabel = get(fields, "productStickerLabel", "")
          .toUpperCase()
          .split(" ")
          .join("_");
        const prefix = get(fields, "prefix", "");
        const colorVariantsInfo = Object.values(
          get(fields, "colorVariantsInfo.fragments", {})
        ).map((fragment: unknown) => {
          const name = get(fragment, "[1].value", "").toLowerCase();
          const hexColorCode = get(fragment, "[2].value", "");

          return { name, hexColorCode };
        });
        const variants = get(fields, "variants", []).map((variant: unknown) => {
          const fields = get(variant, "fields", {});
          const associatedVariantValues = Object.values(
            get(fields, "associatedVariantInfo.simpleFragments", {})
          ).reduce(
            (
              sanitizedAssociatedVariantValues: AssociatedVariantValues,
              fragment: unknown
            ) => {
              sanitizedAssociatedVariantValues.push(
                get(fragment, "variantValue", "").toLowerCase()
              );
              return sanitizedAssociatedVariantValues;
            },
            []
          );
          const images = get(fields, "images", []).map((image: unknown) =>
            flattenContnetfulMedia(image)
          );

          return {
            images,
            associatedVariantValues
          };
        });
        const moreProducts = get(fields, "moreProducts", []).map(
          (product: unknown) => get(product, "fields.productHandle", "")
        );

        return {
          title,
          productHandle,
          care,
          fit,
          details,
          productStickerLabel,
          colorVariantsInfo,
          variants,
          prefix,
          moreProducts
        };
      });
    });
  }
};
